import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    CartesianGrid,
    DefaultTooltipContent,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import dayjs from 'dayjs';
import { useContinuousData } from '../../../../hooks/useContinuousData';
import { useUser } from '../../../../hooks/useUser';
import { getContinuousHeartRateRange as getContinuousRange } from './continuousHeartRate';
import { CONTINUOUS_STEPS_TYPE, normalizeContinuousSteps } from './continuousSteps';
import {
    CONTINUOUS_DISTANCE_TYPE,
    normalizeContinuousDistance
} from './continuousDistance';
import { getDailyRequestRange } from './continuousDailyRange';

const withMissingLabel = (props, missingLabel) => ({
    ...props,
    payload: props.payload?.map((entry) => ({
        ...entry,
        value: entry.value ?? missingLabel
    }))
});

const WearableActivityEvolutionChart = ({
    patientUuid,
    patientCreated,
    period,
    metric = 'steps'
}) => {
    const isDistance = metric === 'distance';
    const { t, i18n } = useTranslation();
    const { user } = useUser();
    const zone = user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const range = useMemo(
        () => getContinuousRange({ period, patientCreated, zone }),
        [period, patientCreated, zone]
    );
    const query = useContinuousData({
        patientUuid,
        dtype: isDistance ? CONTINUOUS_DISTANCE_TYPE : CONTINUOUS_STEPS_TYPE,
        ...(isDistance ? getDailyRequestRange(range, zone) : range)
    });
    const data = useMemo(() => {
        const options = { zone, ...range };
        if (!isDistance) return normalizeContinuousSteps(query.data, options);
        return normalizeContinuousDistance(query.data, options).map((day) => ({
            ...day,
            distance: day.distance === null ? null : day.distance / 1000
        }));
    }, [query.data, zone, range, isDistance]);
    const locale = i18n.resolvedLanguage?.startsWith('pt') ? 'pt-PT' : 'en-GB';
    const dateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'short',
                year: period === 'all' ? '2-digit' : undefined,
                timeZone: 'UTC'
            }),
        [locale, period]
    );
    const numberFormatter = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                maximumFractionDigits: isDistance ? 3 : 0
            }),
        [locale, isDistance]
    );
    const formatDate = (value) => dateFormatter.format(dayjs.utc(value).toDate());
    const key = `userPage.overview.rehabilitation.${isDistance ? 'distanceChart' : 'stepsChart'}`;
    const unit = isDistance ? ' km' : '';
    let content;

    if (query.isPending)
        content = (
            <Box sx={{ height: '100%' }}>
                <Skeleton variant="rounded" height="100%" />
            </Box>
        );
    else if (query.isError)
        content = (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    height: '100%'
                }}
            >
                <Typography color="error" variant="body2">
                    {t(`${key}.loadError`)}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => query.refetch()}>
                    {t('userPage.overview.measurements.retry')}
                </Button>
            </Box>
        );
    else if (!data.some((item) => item[metric] !== null))
        content = (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {t(`${key}.noData`)}
                </Typography>
            </Box>
        );
    else
        content = (
            <Box
                role="img"
                aria-label={t(`${key}.chartLabel`)}
                sx={{ width: '100%', height: '100%' }}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke="#E6EDF2"
                            strokeDasharray="3 3"
                        />
                        <XAxis
                            dataKey="date"
                            axisLine={{ stroke: '#CAD5DD' }}
                            tickLine={false}
                            minTickGap={24}
                            tick={{ fill: '#74838E', fontSize: 10 }}
                            tickFormatter={formatDate}
                        />
                        <YAxis
                            width={isDistance ? 72 : 48}
                            allowDecimals={isDistance}
                            tickFormatter={(value) =>
                                `${numberFormatter.format(value)}${unit}`
                            }
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#74838E', fontSize: 10 }}
                        />
                        <Tooltip
                            filterNull={false}
                            content={(props) => (
                                <DefaultTooltipContent
                                    {...withMissingLabel(props, t(`${key}.missing`))}
                                />
                            )}
                            labelFormatter={formatDate}
                            formatter={(value, name) => [
                                typeof value === 'number'
                                    ? `${numberFormatter.format(value)}${unit}`
                                    : value,
                                name
                            ]}
                            contentStyle={{
                                border: '1px solid #D7E0E6',
                                borderRadius: 6,
                                boxShadow: '0 4px 12px rgba(38, 50, 56, 0.12)',
                                fontSize: 11
                            }}
                        />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            height={24}
                            iconSize={10}
                            wrapperStyle={{ color: '#5E6C76', fontSize: 10 }}
                        />
                        <Bar
                            dataKey={metric}
                            name={t(`${key}.${metric}`)}
                            fill="#0A6FB4"
                            maxBarSize={34}
                            isAnimationActive={false}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        );
    return (
        <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75 }}
            >
                {t(`${key}.subtitle`)}
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>{content}</Box>
        </Box>
    );
};

WearableActivityEvolutionChart.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    patientCreated: PropTypes.string,
    metric: PropTypes.oneOf(['steps', 'distance']),
    period: PropTypes.oneOf(['7d', '30d', '90d', 'all']).isRequired
};

export default WearableActivityEvolutionChart;
